import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Leaf, Mail, CheckCircle, AlertCircle, Loader,
  Eye, EyeOff, ChevronDown, Check, X, Building2, MapPin
} from 'lucide-react';
import PhoneInput from '../components/PhoneInput';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import Navbar from '../components/Navbar';
import { useLanguage } from '../contexts/LanguageContext';
import { getCountryOptions, getCountryLabel } from '../countries';
import { isValidEmail, isValidUrl, isValidPhoneDigits, normalizeUrl } from '../lib/validation';
import { checkPasswordRules } from '../lib/passwordPolicy';
import FieldError from '../components/FieldError';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PasswordRule = ({ ok, label }) => (
  <div className={`flex items-center gap-1.5 text-xs ${ok ? 'text-emerald-600' : 'text-red-500'}`}>
    {ok ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
    <span>{label}</span>
  </div>
);

export default function GreenZoneRegistration() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const tr = language === 'tr';
  const [scrolled, setScrolled] = useState(false);

  const countryOptions = getCountryOptions(language);

  const [form, setForm] = useState({
    full_name: '', email: '', company: '', phone: '', phone_code: '+90',
    country_code: '', city: '', title: '', password: '', password2: '',
    website: '', address: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [showPass2, setShowPass2] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [pass2Touched, setPass2Touched] = useState(false);
  const [countryOpen, setCountryOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');

  // Per-field validation state
  const [gzEmailTouched,   setGzEmailTouched]   = useState(false);
  const [gzPhoneTouched,   setGzPhoneTouched]   = useState(false);
  const [gzWebsiteTouched, setGzWebsiteTouched] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const clearFieldError = (k) => setFieldErrors(prev => { if (!prev[k]) return prev; const n = {...prev}; delete n[k]; return n; });
  const gzEmailValid   = !form.email   || isValidEmail(form.email);
  const gzPhoneValid   = !form.phone   || isValidPhoneDigits(form.phone, { min: 7, max: 15 });
  const gzWebsiteValid = !form.website || isValidUrl(form.website);

  // Registration open/closed check
  const [regOpen, setRegOpen] = useState(null);

  useEffect(() => {
    axios.get(`${API}/auth/registration-status`)
      .then(r => setRegOpen(r.data.green_zone_open !== false))
      .catch(() => setRegOpen(true));
  }, []);

  const passwordRules = checkPasswordRules(form.password);
  const allRulesOk = Object.values(passwordRules).every(Boolean);
  const passwordsMatch = form.password === form.password2;

  const filteredCountries = countryOptions.filter(c =>
    c.label.toLowerCase().includes(countrySearch.toLowerCase())
  );

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (!e.target.closest('[data-country-dropdown]')) setCountryOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setGzEmailTouched(true);
    setGzPhoneTouched(true);
    setGzWebsiteTouched(true);

    const errs = {};
    const reqMsg = tr ? 'Bu alan zorunludur.' : 'This field is required.';
    if (!form.full_name.trim()) errs.full_name = reqMsg;
    if (!form.email.trim())     errs.email     = reqMsg;
    else if (!isValidEmail(form.email)) errs.email = tr ? 'Geçerli bir e-posta giriniz.' : 'Please enter a valid email.';
    if (!form.company.trim())   errs.company   = reqMsg;
    if (!form.password)         errs.password  = reqMsg;
    else if (!allRulesOk)       errs.password  = tr ? 'Şifre tüm kurallara uymalıdır.' : 'Password must meet all rules.';
    if (!form.password2)        errs.password2 = reqMsg;
    else if (!passwordsMatch)   errs.password2 = tr ? 'Şifreler eşleşmiyor.' : 'Passwords do not match.';
    if (form.phone && !gzPhoneValid) {
      errs.phone = tr ? 'Telefon 7–15 rakam içermelidir.' : 'Phone must be 7–15 digits.';
    }
    if (form.website && !gzWebsiteValid) {
      errs.website = tr ? 'Geçerli bir site giriniz (örn. https://firmaniz.com).' : 'Enter a valid site (e.g. https://yourcompany.com).';
    }
    setFieldErrors(errs);
    if (Object.keys(errs).length) {
      if (errs.password) setPasswordTouched(true);
      setError(tr ? 'Lütfen işaretli alanları kontrol ediniz.' : 'Please review the highlighted fields.');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/green-zone-register`, {
        full_name: form.full_name,
        email: form.email,
        company: form.company,
        phone: form.phone ? `${form.phone_code} ${form.phone}` : null,
        country_code: form.country_code || null,
        city: form.city || null,
        title: form.title || null,
        website: normalizeUrl(form.website) || null,
        address: form.address || null,
        password: form.password,
        language: language
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || (tr ? 'Bir hata oluştu.' : 'An error occurred.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-green-50">
      <Navbar scrolled={scrolled} />

      <section className="pt-32 pb-16">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Loading */}
          {regOpen === null && (
            <div className="flex justify-center items-center py-32">
              <div className="animate-spin h-10 w-10 border-4 border-emerald-600 border-t-transparent rounded-full" />
            </div>
          )}

          {/* Registration Closed */}
          {regOpen === false && (
            <div className="text-center py-20">
              <div className="inline-flex items-center justify-center h-24 w-24 rounded-full bg-gray-100 mb-6">
                <Leaf className="h-12 w-12 text-gray-400" />
              </div>
              <h1 className="font-playfair text-3xl md:text-4xl text-[#0A3A5A] font-medium mb-3 tracking-tight">
                {tr ? 'Kayıt Geçici Olarak Kapalı' : 'Registration Temporarily Closed'}
              </h1>
              <p className="text-gray-500 text-lg mb-2">
                {tr
                  ? 'Green Zone kayıt sistemi şu anda geçici olarak kapatılmıştır.'
                  : 'Green Zone registration is temporarily unavailable.'}
              </p>
              <p className="text-gray-400 text-sm mb-8">
                {tr
                  ? 'Lütfen daha sonra tekrar ziyaret ediniz veya organizasyon ile iletişime geçiniz.'
                  : 'Please visit again later or contact the organization.'}
              </p>
              <button onClick={() => navigate('/')} className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-3 rounded-full font-medium transition-colors">
                {tr ? 'Ana Sayfaya Dön' : 'Back to Home'}
              </button>
            </div>
          )}

          {regOpen === true && (<>
          {/* Header */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center justify-center h-20 w-20 rounded-full bg-emerald-600 mb-4">
              <Leaf className="h-12 w-12 text-white" />
            </div>
            <h1 className="font-playfair text-4xl md:text-5xl text-[#0A3A5A] font-medium mb-2 tracking-tight">
              {tr ? 'Green Zone Kayıt' : 'Green Zone Registration'}
            </h1>
            <p className="text-[#4A5568] max-w-md mx-auto">
              {tr
                ? 'Sivil toplum kuruluşları, medya ve şirketler için SummitFlow Green Zone kaydı.'
                : 'SummitFlow Green Zone registration for civil society, media and companies.'}
            </p>
          </div>

          <Card className="border-0 shadow-2xl rounded-3xl">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-emerald-600 flex items-center justify-center">
                  <Building2 className="h-5 w-5 text-white" />
                </div>
                <div>
                  <CardTitle className="text-xl">
                    {tr ? 'Şirket / Kuruluş Başvurusu' : 'Company / Organization Application'}
                  </CardTitle>
                  <p className="text-gray-400 text-sm">
                    {tr ? 'Her e-posta için yalnızca bir başvuru yapılabilir.' : 'Only one application per email address.'}
                  </p>
                </div>
              </div>
            </CardHeader>

            <CardContent className="pt-4">
              {success ? (
                <div className="text-center py-10">
                  <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="h-12 w-12 text-emerald-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    {tr ? 'Başvurunuz Alındı!' : 'Application Submitted!'}
                  </h3>
                  <p className="text-gray-500 mb-6 max-w-sm mx-auto">
                    {tr
                      ? 'Başvurunuz Ultra Admin\'e iletildi. Onaylandıktan sonra Green Zone girişinden sisteme giriş yapabilirsiniz.'
                      : 'Your application has been forwarded to Ultra Admin. Once approved, you can sign in via the Green Zone tab.'}
                  </p>
                  <button
                    onClick={() => navigate('/giris?tab=green')}
                    className="inline-block px-8 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-full transition"
                  >
                    {tr ? 'Giriş Sayfasına Git' : 'Go to Login'}
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                  {error && (
                    <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl p-3 text-red-700 text-sm">
                      <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      <span>{error}</span>
                    </div>
                  )}

                  {/* Ad Soyad */}
                  <div className="space-y-1">
                    <Label>{tr ? 'Ad Soyad' : 'Full Name'} *</Label>
                    <Input
                      data-testid="gz-full-name"
                      placeholder={tr ? 'Adınız Soyadınız' : 'Your Full Name'}
                      value={form.full_name}
                      onChange={e => { set('full_name', e.target.value); clearFieldError('full_name'); }}
                      className={`rounded-xl ${fieldErrors.full_name ? 'border-red-400 focus:border-red-400' : ''}`}
                    />
                    <FieldError msg={fieldErrors.full_name} data-testid="gz-full-name-error" />
                  </div>

                  {/* Email */}
                  <div className="space-y-1">
                    <Label>{tr ? 'E-posta Adresi' : 'Email Address'} *</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                      <Input
                        data-testid="gz-email"
                        type="email"
                        placeholder="email@company.com"
                        value={form.email}
                        onChange={e => { set('email', e.target.value.trim()); clearFieldError('email'); }}
                        onBlur={() => setGzEmailTouched(true)}
                        className={`pl-10 rounded-xl ${(fieldErrors.email || (gzEmailTouched && form.email && !gzEmailValid)) ? 'border-red-400 focus:border-red-400' : ''}`}
                        autoComplete="email"
                      />
                    </div>
                    <FieldError
                      data-testid="gz-email-error"
                      msg={fieldErrors.email || (gzEmailTouched && form.email && !gzEmailValid
                        ? (tr ? 'Geçerli bir e-posta giriniz (örn. ad@firma.com)' : 'Please enter a valid email (e.g. name@company.com)')
                        : null)}
                    />
                  </div>

                  {/* Şirket Adı */}
                  <div className="space-y-1">
                    <Label>{tr ? 'Şirket / Kuruluş Adı' : 'Company/Organization Name'} *</Label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                      <Input
                        data-testid="gz-company"
                        placeholder={tr ? 'Şirket / Kuruluş adı' : 'Company / Organization name'}
                        value={form.company}
                        onChange={e => { set('company', e.target.value); clearFieldError('company'); }}
                        className={`pl-10 rounded-xl ${fieldErrors.company ? 'border-red-400 focus:border-red-400' : ''}`}
                      />
                    </div>
                    <FieldError msg={fieldErrors.company} data-testid="gz-company-error" />
                  </div>

                  {/* Görev */}
                  <div className="space-y-1">
                    <Label>{tr ? 'Görev / Unvan' : 'Title / Position'}</Label>
                    <Input
                      data-testid="gz-title"
                      placeholder={tr ? 'Örn: Genel Müdür, CEO...' : 'e.g. CEO, Manager...'}
                      value={form.title}
                      onChange={e => set('title', e.target.value)}
                      className="rounded-xl"
                    />
                  </div>

                  {/* Telefon */}
                  <div className="space-y-1">
                    <Label>{tr ? 'Telefon' : 'Mobile'} *</Label>
                    <PhoneInput
                      codeValue={form.phone_code}
                      numberValue={form.phone}
                      onCodeChange={v => set('phone_code', v)}
                      onNumberChange={v => { set('phone', v); if (!gzPhoneTouched && v) setGzPhoneTouched(true); }}
                    />
                    {gzPhoneTouched && form.phone && !gzPhoneValid && (
                      <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                        <AlertCircle className="h-3.5 w-3.5" />
                        {tr
                          ? 'Telefon 7–15 rakam içermelidir (harf veya özel karakter kabul edilmez)'
                          : 'Mobile must be 7–15 digits (no letters or symbols)'}
                      </p>
                    )}
                  </div>

                  {/* Şehir + Ülke */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <Label>{tr ? 'Şehir' : 'City'}</Label>
                      <div className="relative">
                        <MapPin className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                        <Input
                          data-testid="gz-city"
                          placeholder={tr ? 'İstanbul...' : 'London...'}
                          value={form.city}
                          onChange={e => set('city', e.target.value)}
                          className="pl-10 rounded-xl"
                        />
                      </div>
                    </div>

                    <div className="space-y-1" data-country-dropdown>
                      <Label>{tr ? 'Ülke' : 'Country'}</Label>
                      <div className="relative">
                        <button
                          type="button"
                          data-testid="gz-country-btn"
                          onClick={() => setCountryOpen(!countryOpen)}
                          className="w-full flex items-center justify-between border border-gray-200 rounded-xl px-4 py-2.5 text-sm bg-white hover:border-gray-400 transition-colors"
                        >
                          <span className={form.country_code ? 'text-gray-900' : 'text-gray-400'}>
                            {form.country_code
                              ? getCountryLabel(form.country_code, language)
                              : (tr ? 'Ülke seçin...' : 'Select country...')}
                          </span>
                          <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${countryOpen ? 'rotate-180' : ''}`} />
                        </button>
                        {countryOpen && (
                          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden">
                            <div className="p-2 border-b">
                              <Input
                                placeholder={tr ? 'Ülke ara...' : 'Search...'}
                                value={countrySearch}
                                onChange={e => setCountrySearch(e.target.value)}
                                className="rounded-lg text-sm"
                                autoFocus
                              />
                            </div>
                            <div className="max-h-48 overflow-y-auto">
                              {filteredCountries.map((c) => (
                                <button
                                  key={c.code}
                                  type="button"
                                  onClick={() => { set('country_code', c.code); setCountryOpen(false); setCountrySearch(''); }}
                                  className={`w-full text-left px-4 py-2.5 text-sm hover:bg-emerald-50 hover:text-emerald-700 transition-colors flex items-center justify-between ${form.country_code === c.code ? 'bg-emerald-50 text-emerald-700 font-medium' : ''}`}
                                >
                                  {c.label}
                                  {form.country_code === c.code && <Check className="h-4 w-4 text-emerald-600" />}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Web Sitesi + Adres (opsiyonel) */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <Label>{tr ? 'Web Sitesi' : 'Website'}</Label>
                      <Input
                        data-testid="gz-website"
                        type="url"
                        inputMode="url"
                        placeholder="https://example.com"
                        value={form.website}
                        onChange={e => set('website', e.target.value.trim())}
                        onBlur={() => setGzWebsiteTouched(true)}
                        className={`rounded-xl ${gzWebsiteTouched && form.website && !gzWebsiteValid ? 'border-red-400 focus:border-red-400' : ''}`}
                        autoComplete="url"
                      />
                      {gzWebsiteTouched && form.website && !gzWebsiteValid && (
                        <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                          <AlertCircle className="h-3.5 w-3.5" />
                          {tr
                            ? 'Geçerli bir site giriniz (örn. firmaniz.com veya https://firmaniz.com)'
                            : 'Enter a valid site (e.g. yourcompany.com or https://yourcompany.com)'}
                        </p>
                      )}
                    </div>
                    <div className="space-y-1">
                      <Label>{tr ? 'Adres' : 'Address'}</Label>
                      <Input
                        data-testid="gz-address"
                        placeholder={tr ? 'Cadde, şehir, ülke' : 'Street, city, country'}
                        value={form.address}
                        onChange={e => set('address', e.target.value)}
                        className="rounded-xl"
                      />
                    </div>
                  </div>

                  {/* Şifre */}
                  <div className="space-y-1">
                    <Label>{tr ? 'Şifre' : 'Password'} *</Label>
                    <div className="relative">
                      <Input
                        data-testid="gz-password"
                        type={showPass ? 'text' : 'password'}
                        placeholder={tr ? 'En az 8 karakter' : 'Min. 8 characters'}
                        value={form.password}
                        onChange={e => { set('password', e.target.value); clearFieldError('password'); if (!passwordTouched) setPasswordTouched(true); }}
                        onBlur={() => setPasswordTouched(true)}
                        className={`pr-10 rounded-xl ${fieldErrors.password ? 'border-red-400' : ''}`}
                      />
                      <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-3 text-gray-400">
                        {showPass ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                    <FieldError msg={fieldErrors.password} data-testid="gz-password-error" />
                    {(passwordTouched || form.password.length > 0) && (
                      <div className="bg-gray-50 rounded-xl p-3 mt-2 grid grid-cols-2 gap-1">
                        <PasswordRule ok={passwordRules.length}    label={tr ? 'En az 8 karakter' : 'Min. 8 characters'} />
                        <PasswordRule ok={passwordRules.uppercase} label={tr ? 'Büyük harf' : 'Uppercase'} />
                        <PasswordRule ok={passwordRules.lowercase} label={tr ? 'Küçük harf' : 'Lowercase'} />
                        <PasswordRule ok={passwordRules.number}    label={tr ? 'Rakam' : 'Number'} />
                        <PasswordRule ok={passwordRules.special}   label={tr ? 'Özel karakter' : 'Special char'} />
                      </div>
                    )}
                  </div>

                  {/* Şifre Tekrar */}
                  <div className="space-y-1">
                    <Label>{tr ? 'Şifre Tekrar' : 'Confirm Password'} *</Label>
                    <div className="relative">
                      <Input
                        data-testid="gz-password2"
                        type={showPass2 ? 'text' : 'password'}
                        placeholder={tr ? 'Şifreyi tekrar girin' : 'Re-enter password'}
                        value={form.password2}
                        onChange={e => { set('password2', e.target.value); clearFieldError('password2'); }}
                        onBlur={() => setPass2Touched(true)}
                        className={`pr-10 rounded-xl ${(fieldErrors.password2 || (pass2Touched && form.password2 && !passwordsMatch)) ? 'border-red-400' : ''}`}
                      />
                      <button type="button" onClick={() => setShowPass2(!showPass2)} className="absolute right-3 top-3 text-gray-400">
                        {showPass2 ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                    <FieldError
                      data-testid="gz-password2-error"
                      msg={fieldErrors.password2 || (pass2Touched && form.password2 && !passwordsMatch
                        ? (tr ? 'Şifreler eşleşmiyor' : 'Passwords do not match')
                        : null)}
                    />
                    {pass2Touched && form.password2 && passwordsMatch && allRulesOk && (
                      <p className="text-emerald-600 text-xs mt-1 flex items-center gap-1">
                        <Check className="h-3.5 w-3.5" />{tr ? 'Şifreler eşleşiyor' : 'Passwords match'}
                      </p>
                    )}
                  </div>

                  <Button
                    data-testid="gz-submit-btn"
                    type="submit"
                    disabled={loading}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-full py-6 text-lg mt-2"
                  >
                    {loading
                      ? <><Loader className="h-5 w-5 mr-2 animate-spin" />{tr ? 'Gönderiliyor...' : 'Submitting...'}</>
                      : (tr ? 'Başvuru Gönder' : 'Submit Application')}
                  </Button>

                  <div className="text-center text-sm text-gray-500">
                    {tr ? 'Zaten hesabınız var mı?' : 'Already have an account?'}{' '}
                    <button type="button" onClick={() => navigate('/giris?tab=green')} className="text-emerald-600 hover:underline font-semibold">
                      {tr ? 'Giriş Yap' : 'Sign In'}
                    </button>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>
          </>)}
        </div>
      </section>
    </div>
  );
}
